import { voucherApi } from "@/main/api";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import ClaimVoucherCard from "@/main/components/shared/claimVoucherCard/claimVoucherCard";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import { VoucherDetailItemResponse } from "@/main/models/response/voucherDetailResponse";
import { Controls } from "@/main/services/constants/constantsControl";
import { getTranslateByKey, isNullUndefined, isShowControlByElementId } from "@/main/services/utils";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import * as S from "./modalClaimVouchers.styles";

export interface IModalClaimVouchersProps {
	isOpen: boolean;
	onClose: () => void;
	reportNumber?: string;
	orderNumber?: string;
	vehicleName?: string;
	guidInsurerCompany?: string;
	quoteNumber?: string;
}

export type ClaimVoucherState = "loading" | "empty" | "error" | "hasData";

function ModalClaimVouchers({
	isOpen,
	onClose,
	reportNumber,
	orderNumber,
	vehicleName,
	guidInsurerCompany,
	quoteNumber,
}: Readonly<IModalClaimVouchersProps>) {
	const hasPermission = isShowControlByElementId(Controls.IdViewVouchers);
	const [viewState, setViewState] = useState<ClaimVoucherState>("empty");
	const [vouchers, setVouchers] = useState<VoucherDetailItemResponse[]>([]);
	const [pageIndex, setPageIndex] = useState<number>(0); // Based on pagination starting at 0/1 depending on backend, but typically Infinite Scroll counts from 1 if it's Naahal Standard. Let's use 1 as requested: "pageIndex: 1"
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const pageSize = 30;

	const fetchVouchers = (page: number, isInitial: boolean) => {
		if (!reportNumber || !guidInsurerCompany) {
			setViewState("empty");
			return;
		}

		if (isInitial) {
			setViewState("loading");
		}

		voucherApi
			.getClaimVouchers({
				claimReportNumber: reportNumber,
				guidInsurerCompany: guidInsurerCompany,
				pageIndex: page,
				pageSize: pageSize,
			})
			.then((response) => {
				const items = response.data?.result?.items || [];
				const total = response.data?.result?.totalRecords || 0;

				if (isInitial) {
					if (items.length > 0) {
						setVouchers(items);
						setTotalRecords(total);
						setViewState("hasData");
					} else {
						setVouchers([]);
						setViewState("empty");
					}
				} else {
					setVouchers((prev) => [...prev, ...items]);
					setTotalRecords(total);
				}
			})
			.catch((error) => {
				if (error?.response?.status === 404) {
					if (isInitial) setViewState("empty");
				} else if (error.name !== "CanceledError" && error.code !== "ERR_CANCELED") {
					if (isInitial) setViewState("error");
				}
			});
	};

	useEffect(() => {
		if (!isOpen || !hasPermission) {
			return;
		}

		setPageIndex(1);
		fetchVouchers(1, true);
	}, [isOpen, reportNumber, guidInsurerCompany, hasPermission]);

	const loadMore = () => {
		const nextPage = pageIndex + 1;
		setPageIndex(nextPage);
		fetchVouchers(nextPage, false);
	};

	if (!hasPermission) {
		return null;
	}

	const title = <S.TitleRow>{getTranslateByKey("information.claimVouchers")}</S.TitleRow>;

	const isOrderValid = !isNullUndefined(orderNumber) && orderNumber !== "" && orderNumber !== "-";
	const orderOrQuoteLabel = isOrderValid
		? getTranslateByKey("information.order")
		: getTranslateByKey("information.quote");
	const orderOrQuoteValue = isOrderValid ? orderNumber : quoteNumber || "-";

	const claimInfo = (
		<S.ClaimInfo>
			<span>
				{`${getTranslateByKey("information.claimNumber")}:`} <S.ClaimInfoValue>{reportNumber || "-"}</S.ClaimInfoValue>
			</span>
			<span>
				{`${orderOrQuoteLabel}:`} <S.ClaimInfoValue>{orderOrQuoteValue}</S.ClaimInfoValue>
			</span>
			<span>
				{`${getTranslateByKey("information.vehicle")}:`} <S.ClaimInfoValue>{vehicleName || "-"}</S.ClaimInfoValue>
			</span>
		</S.ClaimInfo>
	);

	return (
		<KogModalSideComponent open={isOpen} closeModal={onClose} title={title} width="min(31.25rem, calc(100vw - 1rem))">
			<S.Container>
				{claimInfo}

				{viewState === "loading" && (
					<S.LoadingWrapper>
						<LoadingComponent />
					</S.LoadingWrapper>
				)}

				{viewState === "empty" && (
					<S.EmptyWrapper>
						<S.EmptyText>{getTranslateByKey("information.noVouchersToDisplay")}</S.EmptyText>
					</S.EmptyWrapper>
				)}

				{viewState === "error" && (
					<S.ErrorWrapper>
						<S.ErrorIcon />
						<S.ErrorText>{getTranslateByKey("information.xnuupClaimsNotAvailable")}</S.ErrorText>
					</S.ErrorWrapper>
				)}

				{viewState === "hasData" && (
					<S.ContentWrapper id="scrollable-vouchers-container">
						<InfiniteScroll
							dataLength={vouchers.length}
							next={loadMore}
							hasMore={vouchers.length < totalRecords}
							loader={
								<S.LoaderContainer>
									<LoadingComponent />
								</S.LoaderContainer>
							}
							scrollableTarget="scrollable-vouchers-container"
							style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
						>
							{vouchers.map((item, idx) => (
								<ClaimVoucherCard
									key={item.guidVoucher || idx}
									voucherCode={item.voucherCode}
									price={item.price}
									part={item.part}
									voucherTypeName={item.voucherTypeName}
									issueDate={item.issueDate}
									companyName={item.companyName}
									statusName={item.voucherStatusName}
									statusComplement={item.statusComplement}
									availableToCollect={item.availableToCollect}
								/>
							))}
						</InfiniteScroll>
					</S.ContentWrapper>
				)}

				<S.Footer>
					<SecondaryButton type="button" onClick={onClose}>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
				</S.Footer>
			</S.Container>
		</KogModalSideComponent>
	);
}

export default ModalClaimVouchers;
